import { Checkbox, FormControlLabel, Tooltip } from "@mui/material";
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { FadeTransition } from "../../../pages/get-compliant/FadeTransition";
import { safeJsonParse, toggleSelection } from "../../../surveys/util";
import { BaseQuestion, BaseQuestionProps } from "./BaseQuestion";
import { QuestionContainer, QuestionLabel } from "./QuestionContainer";
import clsx from "clsx";

export type SurveyCheckboxOption = {
  key: string;
  label: string;
  title?: string | ((checked: boolean) => string);
  helpText?: string | ReactNode;
  disabled?: true;
  value?: boolean;
};

export type SurveyCheckboxesQuestionProps = BaseQuestion & {
  type: "checkboxes";
  emptyText?: string | ReactNode;
  options: SurveyCheckboxOption[];
  noneOption?: boolean;
  selectAllOption?: boolean;
};

type Props = BaseQuestionProps & {
  question: SurveyCheckboxesQuestionProps;
  noneOption?: boolean;
  selectAllOption?: boolean;
};

const NONE = "none";
const SELECT_ALL = "select-all";

/**
 * Because these survey question options are being passed in,
 * it's not clear if they are being handled carefully (i.e.
 * memoized) by the code using this component. If the values
 * are effectively the same but in a new array, React will
 * still rerun any hooks in which they are a dependency.
 *
 * To sidestep this, we can serialize the incoming options
 * as a string, sorted by key for additional stability using
 * the two helper functions below.
 */

const getOptionsStableKey = (options: SurveyCheckboxOption[]): string => {
  const optionMap: Record<string, SurveyCheckboxOption> = options.reduce(
    (acc, opt) => ({
      ...acc,
      [opt.key]: opt,
    }),
    {},
  );
  return Object.keys(optionMap)
    .sort()
    .map((key) => {
      const val = optionMap[key];
      return `${key}:${val.disabled}:${val.value}`;
    })
    .join("|");
};

const getAnswerStableKey = (answers: string[] | null | undefined): string => {
  if (!answers || !Array.isArray(answers)) {
    return "";
  }
  return answers.sort().join("|");
};

export const SurveyCheckboxesQuestion: React.FC<Props> = ({
  question,
  answer,
  onAnswer,
  collapseTopSpacing,
}) => {
  const { helpText, options } = question;

  const [answering, setAnswering] = useState(false);
  const answers: string[] = useMemo(() => safeJsonParse(answer) ?? [], [answer]);
  const hasAnswer = answers.length > 0;

  const disableByNoneOptionSelected = useMemo(
    () => question.noneOption && hasAnswer && answers.some((a) => a === NONE),
    [question.noneOption, answers, hasAnswer],
  );

  const setAnswer = async (key: string, checked: boolean) => {
    setAnswering(true);
    try {
      let newAnswers: string[];
      if (key === NONE && checked) {
        newAnswers = [NONE];
      } else if (key === SELECT_ALL) {
        if (checked) {
          newAnswers = options.map((c) => c.key);
        } else {
          newAnswers = [];
        }
      } else {
        newAnswers = toggleSelection(answers, key, checked);
        if (!checked) {
          // if we uncheck an answer, we must also uncheck any select-all box, if currently selected
          newAnswers = toggleSelection(newAnswers, SELECT_ALL, false);
        }

        if (question.selectAllOption && newAnswers.length == question.options.length - 1) {
          // if we have selected everything except the select-all box, we must check the select-all box, if not currently selected
          newAnswers = toggleSelection(newAnswers, SELECT_ALL, true);
        }
      }

      await onAnswer(JSON.stringify(newAnswers));
    } finally {
      setAnswering(false);
    }
  };

  // automatic behaviors responding to upstream changes
  // in case the question definition has changed (from a change in upstream answers), strip the local answer if any of answers that are no longer valid options of the question
  const possibleOptionKeys = options.map((v) => v.key);
  const answersToRemove = useMemo(
    () => (answers || []).filter((v) => !possibleOptionKeys.includes(v)),
    [getAnswerStableKey(answers || []), getOptionsStableKey(options)],
  );

  const answersToAdd: string[] = [];

  if (question.selectAllOption) {
    // if the question definition has changed upstream and there are now more question options than before, uncheck select-all (without unchecking everything else)
    if (answers.includes(SELECT_ALL) && options.length > answers.length) {
      answersToRemove.push(SELECT_ALL);
    }
    // if the question definition has changed upstream and there are now exactly one less answer checked than the number of question options,
    // check select-all
    else if (!answers.includes(SELECT_ALL) && answers.length == question.options.length - 1) {
      answersToAdd.push(SELECT_ALL);
    }
  }

  // Get disabled answers with a value that are
  // not already part of the selected answer
  const answersToAutoSet = useMemo(
    () =>
      question.options
        .filter((o) => o.disabled && o.value && !answers.includes(o.key))
        .map((o) => o.key),

    // question.options is an unstable object so we stringify
    // it when setting it as a dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getAnswerStableKey(answers || []), getOptionsStableKey(question.options)],
  );

  // TODO: BURN THIS WITH FIRE
  useEffect(() => {
    // If we render a question that has a disabled answer
    // then we want to ensure it gets set as an answer;
    // this hook calls out to the server with the current
    // answers _plus_ any disabled answers that aren't
    // already in the answers key
    const toAutoset = answersToAutoSet.filter((x) => !answers || !answers.includes(x));

    if (
      !answering &&
      ((question.visible && toAutoset.length > 0) ||
        answersToAdd.length > 0 ||
        answersToRemove.length > 0)
    ) {
      setAnswering(true);
      const newAnswers = (answers || [])
        .concat(...toAutoset, ...answersToAdd)
        .filter((v) => !answersToRemove.includes(v));
      onAnswer(JSON.stringify(newAnswers)).finally(() => {
        setAnswering(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getAnswerStableKey(answers),
    answering,
    question.visible,
    getOptionsStableKey(question.options),
    getAnswerStableKey(answersToAutoSet),
    getAnswerStableKey(answersToAdd),
    getAnswerStableKey(answersToRemove),
  ]);

  return (
    <FadeTransition
      key={`question-${question.slug}`}
      cls="survey-question-fade"
      in={hasAnswer || question.visible}
      unmountOnExit
      appear
    >
      <div
        className={clsx("survey--question", { "survey-question--top-margin": !collapseTopSpacing })}
        ref={question.ref}
      >
        <QuestionContainer>
          <QuestionLabel label={question.question} helpText={helpText} />
          <div className="survey-question--checkboxes">
            {options.map((opt) => {
              let title: string = "";
              if (typeof opt.title === "function") {
                title = opt.title(opt.value ?? answers.includes(opt.key));
              } else if (opt.title) {
                title = opt.title;
              }
              return (
                <div key={opt.key}>
                  <Tooltip title={title}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          checked={opt.value ?? answers.includes(opt.key)}
                          onChange={(e, checked) => {
                            setAnswer(opt.key, checked);
                          }}
                          disabled={
                            answering ||
                            opt.disabled ||
                            (disableByNoneOptionSelected && opt.key != NONE)
                          }
                        />
                      }
                      label={opt.label}
                      className="base-size"
                    />
                  </Tooltip>

                  {opt.helpText && <div className="survey-question--help-text">{opt.helpText}</div>}
                </div>
              );
            })}
            {options.length == 0 && question.emptyText && (
              <div className="survey-question--empty-text">{question.emptyText}</div>
            )}
          </div>
        </QuestionContainer>
      </div>
    </FadeTransition>
  );
};
